import React, { useState } from 'react';
import { TentIcon, MapPinIcon, CalendarIcon, UsersIcon, InfoIcon, ArrowLeftIcon, SaveIcon, XIcon, GlobeIcon, ImageIcon, LinkIcon } from 'lucide-react';
export function FairForm({
  fair = null,
  onSubmit,
  onCancel
}) {
  const [formData, setFormData] = useState({
    id: fair?.id || null,
    name: fair?.name || '',
    location: fair?.location || '',
    startDate: fair?.startDate || '',
    endDate: fair?.endDate || '',
    attendees: fair?.attendees || '',
    description: fair?.description || '',
    website: fair?.website || '',
    contactPerson: fair?.contactPerson || '',
    contactEmail: fair?.contactEmail || '',
    contactPhone: fair?.contactPhone || '',
    logoUrl: fair?.logoUrl || '',
    status: fair?.status || 'upcoming'
  });
  const [errors, setErrors] = useState({});
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Fair name is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    if (!formData.attendees) {
      newErrors.attendees = 'Expected attendees is required';
    } else if (isNaN(formData.attendees) || Number(formData.attendees) <= 0) {
      newErrors.attendees = 'Attendees must be a positive number';
    }
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        attendees: Number(formData.attendees)
      });
    }
  };
  return <div data-id="element-58">
      <div className="flex justify-between items-center mb-6" data-id="element-59">
        <div className="flex items-center" data-id="element-60">
          <button onClick={onCancel} className="mr-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-blue-300 transition-colors" data-id="element-61">
            <ArrowLeftIcon size={20} data-id="element-62" />
          </button>
          <div data-id="element-63">
            <h2 className="text-2xl font-bold text-white flex items-center" data-id="element-64">
              <TentIcon className="mr-2" size={24} data-id="element-65" />
              {fair ? 'Edit Agricultural Fair' : 'Register New Agricultural Fair'}
            </h2>
            <p className="text-blue-300 mt-1" data-id="element-66">
              {fair ? 'Update the details of an existing fair' : 'Enter the details of the agricultural fair you will be attending'}
            </p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-67">
        <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl p-6" data-id="element-68">
          <h3 className="text-lg font-medium text-white mb-4" data-id="element-69">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-70">
            <div data-id="element-71">
              <label htmlFor="name" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-72">
                Fair Name <span className="text-red-400" data-id="element-73">*</span>
              </label>
              <div className="relative" data-id="element-74">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-75">
                  <TentIcon size={18} className="text-blue-400" data-id="element-76" />
                </div>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full bg-white/10 border ${errors.name ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="e.g. Agri Expo 2023" data-id="element-77" />
              </div>
              {errors.name && <p className="mt-1 text-sm text-red-400" data-id="element-78">{errors.name}</p>}
            </div>
            <div data-id="element-79">
              <label htmlFor="location" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-80">
                Location <span className="text-red-400" data-id="element-81">*</span>
              </label>
              <div className="relative" data-id="element-82">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-83">
                  <MapPinIcon size={18} className="text-blue-400" data-id="element-84" />
                </div>
                <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} className={`w-full bg-white/10 border ${errors.location ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="e.g. Madrid, Spain" data-id="element-85" />
              </div>
              {errors.location && <p className="mt-1 text-sm text-red-400" data-id="element-86">{errors.location}</p>}
            </div>
            <div data-id="element-87">
              <label htmlFor="startDate" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-88">
                Start Date <span className="text-red-400" data-id="element-89">*</span>
              </label>
              <div className="relative" data-id="element-90">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-91">
                  <CalendarIcon size={18} className="text-blue-400" data-id="element-92" />
                </div>
                <input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={handleChange} className={`w-full bg-white/10 border ${errors.startDate ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-93" />
              </div>
              {errors.startDate && <p className="mt-1 text-sm text-red-400" data-id="element-94">{errors.startDate}</p>}
            </div>
            <div data-id="element-95">
              <label htmlFor="endDate" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-96">
                End Date <span className="text-red-400" data-id="element-97">*</span>
              </label>
              <div className="relative" data-id="element-98">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-99">
                  <CalendarIcon size={18} className="text-blue-400" data-id="element-100" />
                </div>
                <input type="date" id="endDate" name="endDate" value={formData.endDate} onChange={handleChange} className={`w-full bg-white/10 border ${errors.endDate ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-101" />
              </div>
              {errors.endDate && <p className="mt-1 text-sm text-red-400" data-id="element-102">{errors.endDate}</p>}
            </div>
            <div data-id="element-103">
              <label htmlFor="attendees" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-104">
                Expected Attendees <span className="text-red-400" data-id="element-105">*</span>
              </label>
              <div className="relative" data-id="element-106">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-107">
                  <UsersIcon size={18} className="text-blue-400" data-id="element-108" />
                </div>
                <input type="number" id="attendees" name="attendees" value={formData.attendees} onChange={handleChange} min="1" className={`w-full bg-white/10 border ${errors.attendees ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="e.g. 5000" data-id="element-109" />
              </div>
              {errors.attendees && <p className="mt-1 text-sm text-red-400" data-id="element-110">{errors.attendees}</p>}
            </div>
            <div data-id="element-111">
              <label htmlFor="website" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-112">
                Fair Website
              </label>
              <div className="relative" data-id="element-113">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-114">
                  <GlobeIcon size={18} className="text-blue-400" data-id="element-115" />
                </div>
                <input type="url" id="website" name="website" value={formData.website} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. https://www.agriexpo2023.com" data-id="element-116" />
              </div>
            </div>
          </div>
          <div className="mt-6" data-id="element-117">
            <label htmlFor="description" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-118">
              Description <span className="text-red-400" data-id="element-119">*</span>
            </label>
            <div className="relative" data-id="element-120">
              <div className="absolute top-3 left-3 flex items-start pointer-events-none" data-id="element-121">
                <InfoIcon size={18} className="text-blue-400" data-id="element-122" />
              </div>
              <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={4} className={`w-full bg-white/10 border ${errors.description ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Describe the fair, its focus, and why we're participating..." data-id="element-123" />
            </div>
            {errors.description && <p className="mt-1 text-sm text-red-400" data-id="element-124">{errors.description}</p>}
          </div>
        </div>
        <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl p-6" data-id="element-125">
          <h3 className="text-lg font-medium text-white mb-4" data-id="element-126">
            Additional Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-127">
            <div data-id="element-128">
              <label htmlFor="contactPerson" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-129">
                Contact Person
              </label>
              <input type="text" id="contactPerson" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. John Smith" data-id="element-130" />
            </div>
            <div data-id="element-131">
              <label htmlFor="contactEmail" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-132">
                Contact Email
              </label>
              <input type="email" id="contactEmail" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. contact@agriexpo.com" data-id="element-133" />
            </div>
            <div data-id="element-134">
              <label htmlFor="contactPhone" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-135">
                Contact Phone
              </label>
              <input type="tel" id="contactPhone" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. +1 234 567 890" data-id="element-136" />
            </div>
            <div data-id="element-137">
              <label htmlFor="logoUrl" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-138">
                Fair Logo URL
              </label>
              <div className="relative" data-id="element-139">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-140">
                  <ImageIcon size={18} className="text-blue-400" data-id="element-141" />
                </div>
                <input type="url" id="logoUrl" name="logoUrl" value={formData.logoUrl} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. https://example.com/logo.png" data-id="element-142" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-4" data-id="element-143">
          <button type="button" onClick={onCancel} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center" data-id="element-144">
            <XIcon size={18} className="mr-2" data-id="element-145" />
            Cancel
          </button>
          <button type="submit" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md flex items-center" data-id="element-146">
            <SaveIcon size={18} className="mr-2" data-id="element-147" />
            {fair ? 'Update Fair' : 'Register Fair'}
          </button>
        </div>
      </form>
    </div>;
}